/**
 * Eastern Arabic-Indic (٠-٩) and Extended Arabic-Indic / Persian (۰-۹) → Latin (0-9).
 * Non-digit characters are left unchanged so IDs, mobiles, and mixed strings stay usable.
 */
const ARABIC_INDIC_DIGIT_OFFSET = 0x0660; // '٠'
const EXTENDED_ARABIC_INDIC_DIGIT_OFFSET = 0x06f0; // '۰'

export function containsArabicIndicDigits(value: string): boolean {
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i);
		if (
			(code >= ARABIC_INDIC_DIGIT_OFFSET && code <= ARABIC_INDIC_DIGIT_OFFSET + 9) ||
			(code >= EXTENDED_ARABIC_INDIC_DIGIT_OFFSET && code <= EXTENDED_ARABIC_INDIC_DIGIT_OFFSET + 9)
		) {
			return true;
		}
	}
	return false;
}

export function toLatinDigits(value: string): string {
	if (!containsArabicIndicDigits(value)) {
		return value;
	}

	let out = "";
	for (let i = 0; i < value.length; i++) {
		const code = value.charCodeAt(i);
		if (code >= ARABIC_INDIC_DIGIT_OFFSET && code <= ARABIC_INDIC_DIGIT_OFFSET + 9) {
			out += String.fromCharCode(0x30 + (code - ARABIC_INDIC_DIGIT_OFFSET));
			continue;
		}
		if (code >= EXTENDED_ARABIC_INDIC_DIGIT_OFFSET && code <= EXTENDED_ARABIC_INDIC_DIGIT_OFFSET + 9) {
			out += String.fromCharCode(0x30 + (code - EXTENDED_ARABIC_INDIC_DIGIT_OFFSET));
			continue;
		}
		out += value[i];
	}
	return out;
}

/** Trim + map Arabic-Indic / Persian digits to Latin. Empty / nullish → "". */
export function normalizeDigitString(value: unknown): string {
	if (value === null || value === undefined) {
		return "";
	}
	return toLatinDigits(String(value).trim());
}

/** True when both values match after Arabic-Indic → Latin normalization. */
export function digitStringsEqual(a: unknown, b: unknown): boolean {
	return normalizeDigitString(a) === normalizeDigitString(b);
}

/** Deep-walk JSON-like values and map Arabic-Indic digits in every string. */
export function normalizeArabicIndicDigitsInValue<T>(value: T): T {
	if (typeof value === "string") {
		return toLatinDigits(value) as T;
	}

	if (Array.isArray(value)) {
		return value.map((item) => normalizeArabicIndicDigitsInValue(item)) as T;
	}

	if (
		value !== null &&
		typeof value === "object" &&
		!(value instanceof Date) &&
		!(value instanceof Blob) &&
		!(value instanceof FormData) &&
		!(value instanceof ArrayBuffer) &&
		!(value instanceof File)
	) {
		const source = value as Record<string, unknown>;
		const out: Record<string, unknown> = {};
		for (const key of Object.keys(source)) {
			out[key] = normalizeArabicIndicDigitsInValue(source[key]);
		}
		return out as T;
	}

	return value;
}
